import * as Popover from "@radix-ui/react-popover";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek,format, isSameDay, isSameMonth, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { AlertCircle, Calendar, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import React, { useMemo,useState } from "react";
import { cn } from "src/common/utils";

import { Button } from "./button";
import { Input } from "./input";

interface DateTimePickerProps {
  value: string; // ISO datetime string
  onChange: (value: string) => void;
  className?: string;
}

export const DateTimePicker = React.memo<DateTimePickerProps>(function DateTimePicker({
  value,
  onChange,
  className,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value ? new Date(value) : new Date());

  // Parse the current value
  const selectedDate = value ? new Date(value) : null;
  const selectedTimeHours = selectedDate ? format(selectedDate, "HH") : "12";
  const selectedTimeMinutes = selectedDate ? format(selectedDate, "mm") : "00";

  // Validate that selected time is in the future
  const hasError = useMemo(() => {
    if (!selectedDate) return false;
    const now = new Date();
    return selectedDate.getTime() <= now.getTime();
  }, [selectedDate]);

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const handleDateSelect = (day: Date) => {
    const newDate = new Date(day);
    newDate.setHours(parseInt(selectedTimeHours, 10));
    newDate.setMinutes(parseInt(selectedTimeMinutes, 10));

    // Convert to local datetime string format
    const localDateTime = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    onChange(localDateTime);
  };

  const handleTimeChange = (hours: string, minutes: string) => {
    if (!selectedDate) return;

    const newDate = new Date(selectedDate);
    newDate.setHours(parseInt(hours, 10) || 0);
    newDate.setMinutes(parseInt(minutes, 10) || 0);

    const localDateTime = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    onChange(localDateTime);
  };

  const displayValue = selectedDate
    ? `${format(selectedDate, "MMM d, yyyy")} at ${format(selectedDate, "h:mm a")}`
    : "Select date and time";

  return (
    <div className="space-y-1">
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <button
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/60 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
              hasError ? "border-red-500" : "border-border",
              className
            )}
          >
            <span className={selectedDate ? "text-foreground" : "text-muted-foreground"}>
              {displayValue}
            </span>
            <Calendar className="ml-2 h-4 w-4 opacity-70" />
          </button>
        </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 w-auto max-h-[calc(100vh-20px)] overflow-y-auto custom-scrollbar rounded-md border border-border bg-card p-3 text-foreground shadow-md outline-none"
          align="start"
          side="bottom"
          sideOffset={5}
          collisionPadding={10}
          avoidCollisions={true}
        >
          <div className="flex gap-3">
            {/* Calendar Section */}
            <div className="flex-shrink-0">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="h-6 w-6 p-0 hover:bg-muted"
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <div className="text-xs font-medium text-muted-foreground">
                  {format(currentMonth, "MMMM yyyy")}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="h-6 w-6 p-0 hover:bg-muted"
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-0.5">
                {/* Day headers */}
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <div key={day} className="text-center text-xs text-muted-foreground font-medium py-0.5 w-7">
                    {day}
                  </div>
                ))}

                {/* Calendar days */}
                {calendarDays.map((day, idx) => {
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());

                  return (
                    <button
                      key={idx}
                      onClick={() => handleDateSelect(day)}
                      className={cn(
                        "h-7 w-7 rounded text-xs transition-colors",
                        !isCurrentMonth && "text-muted-foreground",
                        isCurrentMonth && !isSelected && "text-foreground hover:bg-muted",
                        isSelected && "bg-primary text-primary-foreground font-semibold",
                        isToday && !isSelected && "border border-primary"
                      )}
                    >
                      {format(day, "d")}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Picker Section */}
            <div className="flex-shrink-0 border-l border-border pl-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Time</span>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={selectedTimeHours}
                  onChange={(e) => {
                    const val = Math.min(23, Math.max(0, parseInt(e.target.value, 10) || 0));
                    handleTimeChange(val.toString().padStart(2, "0"), selectedTimeMinutes);
                  }}
                  className="w-14 h-8 text-center text-xs px-1"
                  placeholder="HH"
                />
                <span className="text-muted-foreground text-xs">:</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={selectedTimeMinutes}
                  onChange={(e) => {
                    const val = Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0));
                    handleTimeChange(selectedTimeHours, val.toString().padStart(2, "0"));
                  }}
                  className="w-14 h-8 text-center text-xs px-1"
                  placeholder="MM"
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          {hasError && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border text-red-400">
              <AlertCircle className="h-3 w-3" />
              <span className="text-xs">Selected time must be in the future</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end mt-2 pt-2 border-t border-border">
            <Button
              variant="default"
              size="sm"
              onClick={() => setIsOpen(false)}
              disabled={hasError}
              className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed h-7 text-xs"
            >
              Done
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>

      {/* External Error Message */}
      {hasError && !isOpen && (
        <div className="flex items-center gap-1 text-red-400 text-xs">
          <AlertCircle className="h-3 w-3" />
          <span>Selected time must be in the future</span>
        </div>
      )}
    </div>
  );
});
